import { useEffect, useState } from "react";
import {
  getRun,
  listCriterionResponses,
  listInstrumentBlocks,
  listInstrumentCriteria,
  listScaleAnchors,
} from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { isSubmitted } from "../lib/respondent";
import {
  MIN_SCORED_TO_RANK,
  ballFor,
  observationWindow,
  snapshotIndication,
  snapshotReport,
  type SnapshotReport as Model,
  type SnapshotRow,
} from "../lib/snapshot";
import type { Run } from "../lib/types";
import { HarveyBall } from "./HarveyBall";

// The Transformation Snapshot's short report (SPECIFICATION_TRANSFORMATION_SURVEYS
// 011126 V1.0.md, slice 1), one view for the respondent and the Owner: every
// question with its answer, the coverage, and up to three areas to
// investigate. No overall score, no percentage, no maturity level. Slice 2
// (DESIGN_SLICE_SNAPSHOT_SLICE_2 011126 V1.0.md) adds a Harvey ball to every
// answer and one paragraph on what the answers indicate.
//
// It reads the submitted run's own answers: a Snapshot question is its own
// item, so the answer IS the item's result, and the responses are locked once
// the run is submitted.

type State =
  | { kind: "loading" }
  | { kind: "not-yet" }
  | { kind: "error"; message: string }
  | { kind: "ready"; run: Run; model: Model };

export function SnapshotReport({ runId }: { runId: string }) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    void (async () => {
      try {
        const run = await getRun(runId);
        if (!run || !isSubmitted(run.state)) {
          setState({ kind: "not-yet" });
          return;
        }
        const v = run.instrument_version_id;
        const [blocks, criteria, scales, critRows] = await Promise.all([
          listInstrumentBlocks(v),
          listInstrumentCriteria(v),
          listScaleAnchors(v),
          listCriterionResponses(run.id),
        ]);
        const sequenceOf = new Map(blocks.map((b) => [b.id, b.sequence]));
        const titleOf = new Map(blocks.map((b) => [b.id, b.title]));
        const items = criteria.map((c) => ({
          key: c.criterion_key,
          sequence: sequenceOf.get(c.instrument_block_id) ?? c.sequence,
          area: titleOf.get(c.instrument_block_id) ?? c.perspective ?? c.criterion_key,
          question: c.required_content ?? "",
        }));
        const answers = new Map(critRows.map((r) => [r.criterion_id, r]));
        const levels = [...scales.values()][0] ?? [];
        setState({ kind: "ready", run, model: snapshotReport(items, answers, levels) });
      } catch (e) {
        setState({ kind: "error", message: clientError("report", e).message });
      }
    })();
  }, [runId]);

  if (state.kind === "loading") return <p>Preparing your report…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;
  if (state.kind === "not-yet") {
    return (
      <div className="card empty-state">
        <h3>The report is not ready yet</h3>
        <p>The report is produced when the Snapshot is submitted.</p>
      </div>
    );
  }

  const { run, model: m } = state;
  const win = observationWindow(run.created_at);
  const indication = snapshotIndication(m);
  const answer = (r: SnapshotRow, size: number) => {
    const ball = ballFor(r);
    return (
      <>
        <HarveyBall share={ball.share} state={ball.state} size={size} label={r.answer} /> {r.answer}
      </>
    );
  };
  const completed = run.finalized_at
    ? new Date(run.finalized_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <article className="report">
      <h1 style={{ marginBottom: "0.25rem" }}>Transformation Snapshot</h1>
      <p style={{ marginTop: 0, color: "var(--text-muted)" }}>
        The four weeks from {win.from} to {win.to}
        {completed ? ` · completed ${completed}` : ""}
      </p>

      <section className="card">
        {indication && (
          <>
            <h2>What the Snapshot indicates</h2>
            <p>{indication}</p>
          </>
        )}
        <h2>Areas to investigate</h2>
        {m.priorities === null ? (
          <p>
            <strong>Partial profile — too few answers to rank.</strong> {m.scored} of {m.total} questions were scored; at
            least {MIN_SCORED_TO_RANK} are needed to suggest areas to investigate.
          </p>
        ) : m.priorities.length === 0 ? (
          <p>Every scored practice happened consistently in the period.</p>
        ) : (
          <ol style={{ margin: 0 }}>
            {m.priorities.map((r) => (
              <li key={r.key}>
                <strong>{r.area}</strong> — {answer(r, 16)}
              </li>
            ))}
          </ol>
        )}
        <p style={{ marginBottom: 0 }}>
          <strong>Coverage: {m.scored} of {m.total} scored.</strong>
          {m.unscored.length > 0 && <> Not scored: {m.unscored.map((r) => `${r.area} (${r.unscoredReason})`).join("; ")}.</>}
        </p>
      </section>

      <section className="card">
        <h2>Your answers</h2>
        <table className="report-table">
          <thead>
            <tr><th>Area</th><th>Question</th><th>Answer</th></tr>
          </thead>
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.key}>
                <td>{r.area}</td>
                <td>{r.question}</td>
                <td style={{ whiteSpace: "nowrap" }}>{answer(r, 20)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)" }}>
        A self-reported view of how consistently each practice happened in the period. It is not a maturity rating and has
        no overall score.
      </p>
    </article>
  );
}
